import React, { useEffect, useMemo, useState } from 'react';
import { generatePath, useParams, useSearchParams } from 'react-router-dom';
import { connect } from 'react-redux';

// Store
import { Dispatch, RootState } from '../../store/store';

// Constants
import ROUTE from '../../constants/route-constants';
import { INTERVAL_UPDATE_DATA, PAGE_TITLE, SORT_OPTIONS } from '../../constants/common';

// Enums
import SortType from '../../enums/sort-type.enum';
import CycleStatus from '../../enums/cycle-status.enum';

// Components
import InteractiveButton from '../../components/InteractiveButton/InteractiveButton';
import IconPlus from '../../components/Icons/IconPlus/IconPlus';
import SpinnerLoader from '../../components/SpinnerLoader/SpinnerLoader';
import Sort, { SortOption } from '../../components/Sort/Sort';
import { CycleTableWrapper as Cycle } from '../../components/Cycle';
import { useTitle } from '../../hooks/useTitle';
import { useAbilityContext } from '../../components/Can/Can';
import { Action } from '../../enums/action';
import { ScreenService } from '../../store/http/screen.service';
import { ElementSize } from '../../types/element-size';
import { useIntervalOnVisibility } from '../../hooks/useIntervalOnVisibility';
import FaultedCyclesButton from '../../components/FaultedCyclesButton/FaultedCyclesButton';

type ComponentProps = ReturnType<typeof mapDispatch> & ReturnType<typeof mapState>;

enum SearchQueryField {
  Sort = 'sort',
}

type SearchQuery = {
  [SearchQueryField.Sort]: SortType;
};

const ScreenCyclesView: React.FC<ComponentProps> = ({
  getContractById,
  clearDataScreens,
  clearDataContract,
  getScreenById,
  screen,
  contract,
  isViewSettings,
  clearDataCycles,
  getLocationById,
  clearLocationData,
}) => {
  const { clientId, contractId, screenId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cycles, setCycles] = useState<any[]>([]); // Replace 'any' with the actual type of cycles

  const DEFAULT_SEARCH_QUERY: SearchQuery = {
    [SearchQueryField.Sort]: SortType.Newest,
  };

  const ability = useAbilityContext();

  const canCreateCycle = useMemo(() => {
    return ability.can(Action.Read, PAGE_TITLE.ADD_CYCLE);
  }, [ability]);

  const activeCycles = useMemo(() => cycles.filter((cycle) => cycle.status === CycleStatus.Active), [cycles]);

  const fetchLocation = async (id: string) => {
    await getLocationById(id);
  };

  const searchQuery = useMemo(
    () => ({
      [SearchQueryField.Sort]: searchParams.get(SearchQueryField.Sort) || DEFAULT_SEARCH_QUERY[SearchQueryField.Sort],
    }),
    [searchParams],
  ) as unknown as SearchQuery;

  const [isLoadingCycles, setIsLoadingCycles] = useState<boolean>(true);

  const updateSort = (data: SortOption) => {
    setIsLoadingCycles(true);
    clearDataCycles();
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set(SearchQueryField.Sort, data.value);
      return params;
    });

    fetchCycles(screenId);
  };

  const checkQueryParams = () => {
    const searchParamsSort = searchParams.get(SearchQueryField.Sort);

    if (searchParamsSort === null) {
      setSearchParams({
        [SearchQueryField.Sort]: DEFAULT_SEARCH_QUERY[SearchQueryField.Sort],
      });
      return;
    }
  };

  const fetchCycles = async (screenId: string, isPolling = false) => {
    const data = await ScreenService.getAllFullCyclesByScreen({ screenId });

    if (isPolling) {
      console.log('\nPOLLING\n');
      setCycles(prevCycles => {
        return data.map(cycle => {
          const prevCycle = prevCycles.find(c => c.id === cycle.id);

          const mergedIotDataResponse = (cycle.iotDataResponse ?? []).map(item => {
            if (item?.et >= 30) {
              // Keep last known good data for this channel
              const prevItem = (prevCycle?.iotDataResponse ?? []).find(p => p.pk === item.pk);
              console.log(`${item.pk}: NEW et=${item.et} temp=${(item.temp * 9/5) + 32} | PRESERVING prevItem: ${prevItem?.temp ? `temp=${(prevItem.temp * 9/5) + 32}` : 'NO PREV'}`);
              return prevItem ?? item;
            }
            console.log(`${item.pk}: NEW et=${item.et} | ROLLING newItem: ${item.temp ? `temp=${(item.temp * 9/5) + 32}` : 'NO TEMP'}`);
            return item;
          });

          const mergedChannelsIot = Object.fromEntries(
            Object.entries(cycle.channelsIot ?? {}).map(([key, ch]) => {
              if (ch.iotData.rawItem?.et >= 30) {
                // Keep last known good channel data
                const prevChannel = prevCycle?.channelsIot?.[key];
                return [key, prevChannel ?? ch];
              }
              return [key, ch];
            })
          );

          return {
            ...cycle,
            iotDataResponse: mergedIotDataResponse,
            channelsIot: mergedChannelsIot,
          };
        });
      });
    } else {
      // On first load: show all channels but strip temp & sp when et >= 30
      const processed = data.map(cycle => ({
        ...cycle,
        iotDataResponse: (cycle.iotDataResponse ?? []).map(item => {
          return item?.et >= 30
            ? { ...item, temp: '-', sp: '-' }
            : item;
        }),
        channelsIot: Object.fromEntries(
          Object.entries(cycle.channelsIot ?? {}).map(([key, ch]) => {
            if (ch.iotData.rawItem?.et >= 30) {
              return [key, { 
                ...ch, 
                temperature: '-', 
                iotData: { 
                  ...ch.iotData, 
                  rawItem: { ...ch.iotData.rawItem, temp: '-', sp: '-' } 
                } 
              }];
            }
            return [key, ch];
          })
        ),
      }));
      setCycles(processed);
    }

    setIsLoadingCycles(false);
  };

  const fetchContract = async (id: string) => {
    await getContractById(id);
  };

  const fetchScreen = async (id: string) => {
    await getScreenById({ screenId: id });
  };

  const fetchData = async (contractId: string, screenId: string, clientId: string) => {
    await fetchLocation(clientId);
    await fetchContract(contractId);
    await fetchCycles(screenId);
    await fetchScreen(screenId);
  };

  const resetData = () => {
    clearDataScreens();
    clearDataContract();
    clearDataCycles();
    clearLocationData();
  };

  useEffect(() => {
    checkQueryParams();
    fetchData(contractId, screenId, clientId);

    return () => {
      resetData();
    };
  }, []);

  const enabled = !(!screen || isViewSettings || isLoadingCycles);

  useIntervalOnVisibility({
    callback: () => fetchCycles(screenId, true),
    delay: INTERVAL_UPDATE_DATA,
    dependencies: [screen, isLoadingCycles, isViewSettings],
    enabled,
  });

  const title = contract?.name && screen?.name ? `${contract?.name} - ${screen?.name}` : 'undefined';

  useTitle(title);

  return (
    <>
      {!screen && (
        <div className="flex h-full w-full items-center justify-center">
          <SpinnerLoader />
        </div>
      )}
      {screen && (
        <div className="p-screen-cycles-view h-full w-full">
          <div className="p-screen-cycles-view__top flex flex-col gap-y-4 pb-4">
            <div className="p-screen-cycles-view__top-header flex items-center justify-between">
              <div className="p-screen-cycles-view__header-right-part flex flex-row items-center gap-x-4 text-title-md2 font-semibold text-black dark:text-white truncate pr-4">
                <h2 className="text-title-md md:text-title-md2 truncate font-semibold max-xsm:pb-2 text-black dark:text-white">{title}</h2>
              </div>
              <div className="flex flex-row items-center gap-x-2">
                <FaultedCyclesButton cycles={activeCycles} />
                {canCreateCycle && (
                  <InteractiveButton
                    as="a"
                    className='text-nowrap min-w-fit'
                    iconLeft={<IconPlus size={ElementSize.XS} />}
                    href={generatePath(ROUTE.ADD_SCREEN_CYCLE, { clientId, contractId, screenId })}
                    text="Add new cycle"
                    target="_blank"
                  />
                )}
              </div>
            </div>
            <div className="p-screen-cycles-view__top-indicators flex flex-row items-center justify-between">
              <div className="p-cycles-view__sort grid-cols grid grid-flow-col grid-cols-[auto_minmax(120px,_auto)] items-center gap-x-2 text-base">
                <span className="p-cycles-view__description">Sorting:</span>
                <Sort
                  options={SORT_OPTIONS}
                  selectedValue={searchQuery[SearchQueryField.Sort]}
                  changeOption={updateSort}
                />
              </div>
            </div>
          </div>
          {isLoadingCycles && (
            <div className="flex h-full w-full items-center justify-center">
              <SpinnerLoader />
            </div>
          )}
          {!isLoadingCycles && (
            <div className="p-screen-cycles-view__body columns-2xl gap-x-3">
              {cycles.map((cycle) => (
                <div
                  className="p-screen-cycles-view__cycle_wrapper flex h-full break-inside-avoid py-1.5"
                  key={cycle.id}
                >
                  <Cycle
                    cycle={cycle}
                    sort={searchQuery[SearchQueryField.Sort]}
                    isViewActions={cycle.status === CycleStatus.Active}
                    clientId={clientId}
                    contractId={contractId}
                    screenId={screenId}
                    attachmentSaving={false}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
};

const mapState = (state: RootState) => ({
  screen: state.screens.screen,
  isViewSettings: state.cycles.isViewSettings,
  contract: state.contracts.contract,
});

const mapDispatch = (dispatch: Dispatch) => ({
  getContractById: dispatch.contracts.getContract,
  getScreenById: dispatch.screens.getOneById,
  clearDataScreens: dispatch.screens.resetList,
  clearDataContract: dispatch.contracts.resetOne,
  clearDataCycles: dispatch.cycles.resetList,
  getLocationById: dispatch.locations.getLocation,
  clearLocationData: dispatch.locations.resetOne,
});

export default connect(mapState, mapDispatch)(ScreenCyclesView);
